import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

type Common = { tooltip?: string; className?: string }

export type BackButtonProps =
  | (Common & { to: string; onClick?: never })
  | (Common & { onClick: () => void; to?: never })

const baseClasses =
  'flex items-center justify-center w-8 h-8 rounded-lg border border-[#E2E5EA] ' +
  'bg-white hover:bg-page-bg transition-colors flex-shrink-0 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-hero-red'

/**
 * Canonical app-wide back button — icon-only square chip matching the
 * CRC Evans design system "small icon button" recipe (see
 * docs/CRCE-AI-Design-System.md). Use as a navigation `Link` via `to`, or
 * as an imperative `button` via `onClick`. Always rendered top-left of a
 * page header next to the title block.
 */
export function BackButton(props: BackButtonProps) {
  const tooltip = props.tooltip ?? 'Back'
  const className = [baseClasses, props.className ?? ''].join(' ').trim()
  const icon = <ArrowLeft size={14} className="text-blueprint-blue" aria-hidden />

  if ('to' in props && props.to !== undefined) {
    return (
      <Link to={props.to} title={tooltip} aria-label={tooltip} className={className}>
        {icon}
      </Link>
    )
  }

  return (
    <button
      type="button"
      onClick={props.onClick}
      title={tooltip}
      aria-label={tooltip}
      className={className}
    >
      {icon}
    </button>
  )
}

export default BackButton
